import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from './Header';
import LoadingSpinner from './LoadingSpinner';
import { chatsAPI, photosAPI, readStatusAPI, usersAPI } from '../services/api';
import { wsService } from '../services/websocket';
import { Chat, ChatMessage, User } from '../types';
import UserDetailsCard from './UserDetailsCard';
import './Chats.css';

const Chats: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [parseMode, setParseMode] = useState<'HTML' | 'Markdown'>('HTML');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [userAvatars, setUserAvatars] = useState<{[key: number]: string}>({});
  const [loadingAvatars, setLoadingAvatars] = useState<{[key: number]: boolean}>({});
  const [lastReadMessageIds, setLastReadMessageIds] = useState<{[key: number]: number}>({});
  const [unreadCounts, setUnreadCounts] = useState<{[key: number]: number}>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatsListRef = useRef<HTMLDivElement>(null);
  const isLoadingOlderRef = useRef(false);
  const scrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const prevSearchTermRef = useRef(searchTerm);
  const searchTermJustChangedRef = useRef(false);

  // Проверка является ли сообщение командой
  const isCommand = (message: ChatMessage | string): boolean => {
    // Если передан объект сообщения, используем поле isCommand из базы данных
    if (typeof message === 'object' && message.isCommand !== undefined) {
      return message.isCommand;
    }
    
    // Если передан текст, используем старую логику для обратной совместимости
    const text = typeof message === 'string' ? message : message.text;
    const trimmedText = text.trim();
    
    // Команды бота
    const botCommands = [
      'Вибрати групу',
      'Відправити розклад', 
      'Автоматичне відправлення',
      'Встановити час',
      'Допомога',
      'Сьогодні',
      'Завтра',
      'Назад',
      'Зупинити відправлення',
      'Відновити відправлення',
      '/start',
      '/help'
    ];
    
    return botCommands.includes(trimmedText) ||
           trimmedText.startsWith('/') || 
           trimmedText.startsWith('!') || 
           trimmedText.match(/^(start|help|menu|settings|profile|info|status|ping|test)$/i) !== null;
  };

  // Подсчет непрочитанных сообщений для чата (не используется, но может понадобиться)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getUnreadCount = useCallback(async (chat: Chat): Promise<number> => {
    const lastReadId = lastReadMessageIds[chat.chatId] || 0;
    
    // Для всех чатов используем точный подсчет через API
    try {
      const response = await chatsAPI.getUnreadCount(chat.chatId, lastReadId);
      return response.unreadCount;
    } catch (error) {
      console.error('Ошибка при получении количества непрочитанных сообщений:', error);
      // Fallback к старой логике в случае ошибки
      if (chat.lastMessage && chat.lastMessage.messageId > lastReadId) {
        if (chat.lastMessage.sender === 'user' && !isCommand(chat.lastMessage)) {
          return Math.min(Math.max(1, chat.lastMessage.messageId - lastReadId), 99);
        }
      }
      return 0;
    }
  }, [lastReadMessageIds]);

  // Обновление счетчиков непрочитанных сообщений для всех чатов
  const updateUnreadCounts = useCallback(async () => {
    const newUnreadCounts: {[key: number]: number} = {};
    for (const chat of chats) {
      const lastReadId = lastReadMessageIds[chat.chatId] || 0;
      
      try {
        const response = await chatsAPI.getUnreadCount(chat.chatId, lastReadId);
        newUnreadCounts[chat.chatId] = response.unreadCount;
      } catch (error) {
        console.error('Ошибка при получении количества непрочитанных сообщений:', error);
        // Fallback к старой логике в случае ошибки
        if (chat.lastMessage && chat.lastMessage.messageId > lastReadId) {
          if (chat.lastMessage.sender === 'user' && !isCommand(chat.lastMessage)) {
            newUnreadCounts[chat.chatId] = Math.min(Math.max(1, chat.lastMessage.messageId - lastReadId), 99);
          } else {
            newUnreadCounts[chat.chatId] = 0;
          }
        } else {
          newUnreadCounts[chat.chatId] = 0;
        }
      }
    }
    setUnreadCounts(newUnreadCounts);
  }, [chats, lastReadMessageIds]);

  // Подсчет общего количества непрочитанных сообщений во всех чатах
  const getTotalUnreadCount = (): number => {
    return Object.values(unreadCounts).reduce((total, count) => total + count, 0);
  };

  // Загрузка списка чатов (pageOverride — при смене поиска грузим с 1-й страницы)
  const loadChats = useCallback(async (pageOverride?: number) => {
    const currentPage = pageOverride ?? page;
    try {
      setLoading(true);
      const response = await chatsAPI.getChats(currentPage, 50, searchTerm);
      setHasMoreChats(response.pagination.page < response.pagination.pages);
      if (currentPage === 1) {
        setChats(response.chats);
        setTimeout(() => updateUnreadCounts(), 100);
      } else {
        setChats(prev => [...prev, ...response.chats]);
        setTimeout(() => updateUnreadCounts(), 100);
      }
    } catch (error) {
      console.error('Ошибка загрузки чатов:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  // Загрузка сообщений чата
  const loadMessages = async (chatId: number, pageNum: number = 1) => {
    try {
      setMessagesLoading(true);
      const response = await chatsAPI.getChatMessages(chatId, pageNum, 50);
      if (pageNum === 1) {
        setMessages(response.messages);
      } else {
        // Сохраняем позицию прокрутки перед добавлением старых сообщений сверху
        if (messagesContainerRef.current) {
          const el = messagesContainerRef.current;
          scrollRestoreRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop };
          isLoadingOlderRef.current = true;
        }
        setMessages(prev => [...response.messages, ...prev]);
      }
      setHasMoreMessages(response.pagination.page < response.pagination.pages);
    } catch (error) {
      console.error('Ошибка загрузки сообщений:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Отправка сообщения
  const sendMessage = async () => {
    if (!selectedChat || !newMessage.trim() || sending) return;

    try {
      setSending(true);
      const response = await chatsAPI.sendMessage(selectedChat.chatId, {
        message: newMessage.trim(),
        parseMode: parseMode
      });
      
      // Добавляем новое сообщение в список только если оно успешно отправлено
      if (response.success && response.message) {
        setMessages(prev => {
          // Дополнительная проверка на дублирование при локальном добавлении
          const messageExists = prev.some(msg => 
            msg.messageId === response.message.messageId && msg.sender === response.message.sender
          );
          
          if (messageExists) {
            console.log('Message already exists locally, skipping:', response.message.messageId);
            return prev;
          }
          
          return [...prev, response.message];
        });
        
        setNewMessage('');
        
        // Обновляем последнее сообщение в списке чатов
        setChats(prev => prev.map(chat => 
          chat.chatId === selectedChat.chatId 
            ? { ...chat, lastMessage: response.message, messageCount: chat.messageCount + 1 }
            : chat
        ));

        // Прокручиваем к концу
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
    } finally {
      setSending(false);
    }
  };

  // Обработка нажатия Enter для отправки сообщения
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Выбор чата
  const selectChat = (chat: Chat) => {
    setSelectedChat(chat);
    setMessagesPage(1);
    loadMessages(chat.chatId, 1);
    
    // Отмечаем сообщения как прочитанные через небольшую задержку
    setTimeout(() => {
      markMessagesAsRead(chat.chatId);
    }, 1000);
  };

  // Подгрузка следующей страницы чатов при прокрутке списка
  const loadMoreChats = () => {
    if (!loading && hasMoreChats) {
      setPage(prev => prev + 1);
    }
  };

  // Обработка прокрутки списка чатов — подгрузка при достижении конца
  const handleChatsListScroll = () => {
    const el = chatsListRef.current;
    if (!el || loading || !hasMoreChats) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      loadMoreChats();
    }
  };

  // Загрузка старых сообщений
  const loadMoreMessages = () => {
    if (selectedChat && !messagesLoading && hasMoreMessages) {
      const nextPage = messagesPage + 1;
      setMessagesPage(nextPage);
      loadMessages(selectedChat.chatId, nextPage);
    }
  };

  // Обработка прокрутки для загрузки старых сообщений
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop } = messagesContainerRef.current;
      if (scrollTop === 0 && hasMoreMessages && !messagesLoading) {
        loadMoreMessages();
      }
    }
  };

  // Загрузка аватарки пользователя
  const loadUserAvatar = useCallback(async (chatId: number) => {
    // Проверяем, не загружается ли уже аватар или не загружен ли он
    if (userAvatars[chatId] || loadingAvatars[chatId]) return;
    
    try {
      setLoadingAvatars(prev => ({ ...prev, [chatId]: true }));
      const response = await photosAPI.getUserPhotos(chatId.toString());
      if (response.success && response.photos && response.photos.length > 0) {
        const firstPhoto = response.photos[0];
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.denisphones.org';
        const avatarUrl = firstPhoto.url.startsWith('http') 
          ? firstPhoto.url 
          : `${apiBaseUrl}${firstPhoto.url}`;
        setUserAvatars(prev => ({ ...prev, [chatId]: avatarUrl }));
      }
    } catch (error) {
      console.error('Failed to load user avatar:', error);
    } finally {
      setLoadingAvatars(prev => ({ ...prev, [chatId]: false }));
    }
  }, [userAvatars, loadingAvatars]);

  // Отметка сообщений как прочитанных
  const markMessagesAsRead = useCallback(async (chatId: number) => {
    if (selectedChat && selectedChat.chatId === chatId && messages.length > 0) {
      const lastMessageId = Math.max(...messages.map(msg => msg.messageId));
      setLastReadMessageIds(prev => ({ ...prev, [chatId]: lastMessageId }));
      
      // Обновляем счетчик непрочитанных в списке чатов
      setChats(prev => prev.map(chat => 
        chat.chatId === chatId 
          ? { ...chat, unreadCount: 0, lastReadMessageId: lastMessageId }
          : chat
      ));
      
      // Обновляем счетчик непрочитанных в состоянии
      setUnreadCounts(prev => ({ ...prev, [chatId]: 0 }));
      
      // Сохраняем статус прочтения в базе данных
      try {
        await readStatusAPI.updateReadStatus(chatId, lastMessageId);
      } catch (error) {
        console.error('Failed to update read status:', error);
      }
    }
  }, [selectedChat, messages]);

  // Отметка всех сообщений как прочитанных
  const markAllAsRead = async () => {
    
    // Сначала формируем объект с данными для отправки на сервер
    const chatReadStatuses: {[chatId: number]: number} = {};
    
    chats.forEach(chat => {
      
      if (!chat.lastMessage || !chat.lastMessage.messageId) {
        console.warn('Chat has no lastMessage or messageId:', chat);
        return;
      }
      
      const lastReadMessageId = chat.lastMessage.messageId;
      chatReadStatuses[chat.chatId] = lastReadMessageId;
      console.log(`Added chat ${chat.chatId} with messageId ${lastReadMessageId}`);
    });
    
    // Проверяем, есть ли что отправлять
    if (Object.keys(chatReadStatuses).length === 0) {
      console.warn('No chats to mark as read!');
      return;
    }
    
    // Обновляем локальное состояние чатов
    setChats(prev => prev.map(chat => ({
      ...chat,
      unreadCount: 0,
      lastReadMessageId: chat.lastMessage?.messageId || 0
    })));
    
    // Обновляем локальное состояние статуса прочтения
    setLastReadMessageIds(chatReadStatuses);
    
    // Сохраняем статус прочтения в базе данных
    try {
      const response = await readStatusAPI.markAllAsRead(chatReadStatuses);
      console.log('Server response:', response);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Форматирование времени
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Вчера';
    } else if (days < 7) {
      return date.toLocaleDateString('ru-RU', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    }
  };

  // Открытие деталей пользователя по клику на имя в заголовке чата
  const openUserDetails = useCallback(async (chatId: number) => {
    try {
      setUserDetailsLoading(true);
      const response = await usersAPI.getUsers(1, 1, { search: String(chatId) });
      const user = response.users.find((u: User) => String(u.chatId) === String(chatId));
      setSelectedUser(user || null);
    } catch (err) {
      console.error('Ошибка загрузки деталей пользователя:', err);
      setSelectedUser(null);
    } finally {
      setUserDetailsLoading(false);
    }
  }, []);

  // Прокрутка к концу при загрузке новых сообщений; при подгрузке старых — сохраняем позицию
  useEffect(() => {
    if (!selectedChat || messages.length === 0) return;
    const container = messagesContainerRef.current;
    if (isLoadingOlderRef.current && container && scrollRestoreRef.current) {
      const old = scrollRestoreRef.current;
      const heightDiff = container.scrollHeight - old.scrollHeight;
      container.scrollTop = old.scrollTop + heightDiff;
      isLoadingOlderRef.current = false;
      scrollRestoreRef.current = null;
    } else if (!isLoadingOlderRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        markMessagesAsRead(selectedChat.chatId);
      }, 100);
    }
  }, [messages.length, selectedChat, markMessagesAsRead]);

  // Загрузка аватарок для чатов
  useEffect(() => {
    if (chats.length > 0) {
      chats.forEach(chat => {
        // Загружаем аватар только если он еще не загружен
        if (!userAvatars[chat.chatId] && !loadingAvatars[chat.chatId]) {
          loadUserAvatar(chat.chatId);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats.length]); // Используем только длину массива, а не весь массив

  // Загрузка статуса прочтения при монтировании
  useEffect(() => {
    const loadReadStatuses = async () => {
      try {
        const response = await readStatusAPI.getReadStatuses();
        if (response.success) {
          setLastReadMessageIds(response.readStatuses);
        }
      } catch (error) {
        console.error('Failed to load read statuses:', error);
      }
    };
    
    loadReadStatuses();
  }, []);

  // Автовыбор чата по chatId из URL-параметра
  useEffect(() => {
    const chatIdParam = searchParams.get('chatId');
    if (!chatIdParam) return;

    const numericChatId = parseInt(chatIdParam, 10);
    if (isNaN(numericChatId)) {
      setSearchParams({});
      return;
    }

    const openChatById = async () => {
      // Сначала ищем чат в уже загруженном списке
      const existing = chats.find(c => c.chatId === numericChatId);
      if (existing) {
        selectChat(existing);
        setSearchParams({});
        return;
      }

      // Если не нашли — запрашиваем по числовому chatId через поиск
      try {
        setLoading(true);
        const response = await chatsAPI.getChats(1, 50, String(numericChatId));
        const found = response.chats.find((c: Chat) => c.chatId === numericChatId);
        if (found) {
          // Объединяем с текущим списком (не сбрасываем)
          setChats(prev => {
            const already = prev.some(c => c.chatId === found.chatId);
            return already ? prev : [found, ...prev];
          });
          selectChat(found);
        }
      } catch (err) {
        console.error('Ошибка при поиске чата по chatId:', err);
      } finally {
        setLoading(false);
        setSearchParams({});
      }
    };

    openChatById();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Загрузка чатов при смене страницы или поиска
  useEffect(() => {
    if (prevSearchTermRef.current !== searchTerm) {
      prevSearchTermRef.current = searchTerm;
      setPage(1);
      setHasMoreChats(true);
      loadChats(1);
      searchTermJustChangedRef.current = true;
      return;
    }
    if (searchTermJustChangedRef.current) {
      searchTermJustChangedRef.current = false;
      return;
    }
    loadChats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  // Загрузка чатов при монтировании и WebSocket
  useEffect(() => {
    // Подключаемся к WebSocket
    wsService.connect().catch(console.error);
    
    // Обработчики состояния WebSocket
    const handleWsConnected = () => {
      setWsConnected(true);
      console.log('✅ WebSocket connected for chats');
    };
    
    const handleWsDisconnected = () => {
      setWsConnected(false);
      console.log('❌ WebSocket disconnected for chats');
    };
    
    // Обработчики WebSocket событий
    const handleNewChatMessage = (message: ChatMessage) => {
      console.log('New chat message received:', message);
      
      // Обновляем список чатов
      setChats(prev => {
        const existingChatIndex = prev.findIndex(chat => chat.chatId === message.chatId);
        
        if (existingChatIndex !== -1) {
          // Обновляем существующий чат
          const updatedChats = [...prev];
          const chat = updatedChats[existingChatIndex];
          
          // Увеличиваем счетчик непрочитанных только для текстовых сообщений от пользователей
          const isNewUnreadMessage = message.sender === 'user' && !isCommand(message);
          const newUnreadCount = isNewUnreadMessage ? (chat.unreadCount || 0) + 1 : chat.unreadCount || 0;
          
          updatedChats[existingChatIndex] = {
            ...chat,
            lastMessage: message,
            messageCount: chat.messageCount + 1,
            unreadCount: newUnreadCount
          };
          
          // Перемещаем чат в начало списка
          const updatedChat = updatedChats.splice(existingChatIndex, 1)[0];
          return [updatedChat, ...updatedChats];
        } else {
          // Создаем новый чат (если сообщение от пользователя)
          if (message.sender === 'user') {
            const unreadCount = !isCommand(message) ? 1 : 0;
            const newChat: Chat = {
              chatId: message.chatId,
              userName: message.userName,
              group: message.group,
              lastMessage: message,
              messageCount: 1,
              unreadCount: unreadCount
            };
            return [newChat, ...prev];
          }
        }
        
        return prev;
      });
      
      // Обновляем счетчики непрочитанных в состоянии
      if (message.sender === 'user' && !isCommand(message)) {
        setUnreadCounts(prev => ({
          ...prev,
          [message.chatId]: (prev[message.chatId] || 0) + 1
        }));
      }
      
      // Если это сообщение в текущем открытом чате, проверяем, не дублируется ли оно
      if (selectedChat && selectedChat.chatId === message.chatId) {
        setMessages(prev => {
          // Проверяем, есть ли уже такое сообщение (по messageId и sender)
          const messageExists = prev.some(msg => 
            msg.messageId === message.messageId && msg.sender === message.sender
          );
          
          if (messageExists) {
            console.log('Message already exists, skipping duplicate:', message.messageId);
            return prev;
          }
          
          return [...prev, message];
        });
        
        // Прокручиваем к новому сообщению
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    };
    
    
    // Подписываемся на события
    wsService.on('connected', handleWsConnected);
    wsService.on('disconnected', handleWsDisconnected);
    wsService.on('new_chat_message', handleNewChatMessage);
    wsService.on('unauthorized', () => {
      console.log('WebSocket unauthorized, redirecting to login');
      // Перенаправление уже обработано в WebSocket сервисе
    });
    
    // Очистка подписок при размонтировании
    return () => {
      wsService.off('connected', handleWsConnected);
      wsService.off('disconnected', handleWsDisconnected);
      wsService.off('new_chat_message', handleNewChatMessage);
      wsService.off('unauthorized', () => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat]);

  return (
    <div className="chats-page">
      <Header title="Чаты" />
      <main className="main-content">
        <div className="chats-container">
          {/* Список чатов */}
          <div className="chats-sidebar">
            <div className="chats-header">
              <div className="chats-header-content">
                <div className="chats-title-section">
                  <h3>Чаты ({chats.length})</h3>
                  <div className="chats-actions">
                    <div className="search-container">
                      <input
                        type="text"
                        placeholder="Поиск по имени или группе..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                      />
                      {searchTerm && (
                        <button 
                          className="clear-search"
                          onClick={() => setSearchTerm('')}
                          title="Очистить поиск"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <button 
                      className="mark-all-read-btn"
                      onClick={markAllAsRead}
                      title="Отметить все как прочитанные"
                      disabled={getTotalUnreadCount() === 0}
                    >
                      ✓ Прочитать все ({getTotalUnreadCount()})
                    </button>
                  </div>
                </div>
                <div className={`ws-status ${wsConnected ? 'connected' : 'disconnected'}`}>
                  <span className="ws-indicator"></span>
                  {wsConnected ? 'Подключено' : 'Отключено'}
                </div>
              </div>
            </div>
            <div
              className="chats-list"
              ref={chatsListRef}
              onScroll={handleChatsListScroll}
            >
              {loading && page === 1 ? (
                <div className="loading-container">
                  <LoadingSpinner />
                </div>
              ) : chats.length === 0 ? (
                <div className="empty-state">
                  <p>Чатов пока нет</p>
                </div>
              ) : (
                <>
                {chats.map((chat) => (
                  <div
                    key={chat.chatId}
                    className={`chat-item ${selectedChat?.chatId === chat.chatId ? 'selected' : ''}`}
                    onClick={() => selectChat(chat)}
                  >
                    <div className="chat-avatar">
                      {userAvatars[chat.chatId] ? (
                        <img 
                          src={userAvatars[chat.chatId]} 
                          alt={chat.userName}
                          className="avatar-image"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                            if (nextElement) {
                              nextElement.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div className="avatar-fallback" style={{ display: userAvatars[chat.chatId] ? 'none' : 'flex' }}>
                        {loadingAvatars[chat.chatId] ? (
                          <div className="avatar-loading">📷</div>
                        ) : (
                          chat.userName.charAt(0).toUpperCase()
                        )}
                      </div>
                    </div>
                    <div className="chat-info">
                      <div className="chat-name">
                        {chat.userName}
                        {chat.group && <span className="chat-group"> • {chat.group}</span>}
                      </div>
                      <div className="chat-last-message">
                        {((chat.lastMessage?.text ?? '').length > 50)
                          ? `${(chat.lastMessage?.text ?? '').substring(0, 50)}...`
                          : (chat.lastMessage?.text ?? '')
                        }
                      </div>
                      <div className="chat-meta">
                        <span className="chat-time">{formatTime(chat.lastMessage?.timestamp)}</span>
                        {unreadCounts[chat.chatId] > 0 && (
                          <span className="chat-count unread">{unreadCounts[chat.chatId]}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && page > 1 && (
                  <div className="loading-more-chats">
                    <LoadingSpinner />
                    <span>Загрузка чатов...</span>
                  </div>
                )}
                {!hasMoreChats && chats.length > 0 && (
                  <div className="chats-list-end">Все чаты загружены</div>
                )}
                </>
              )}
            </div>
          </div>

          {/* Область сообщений */}
          <div className="messages-area">
            {selectedChat ? (
              <>
                {/* Заголовок чата */}
                <div className="messages-header">
                  <div className="chat-avatar">
                    {userAvatars[selectedChat.chatId] ? (
                      <img 
                        src={userAvatars[selectedChat.chatId]} 
                        alt={selectedChat.userName}
                        className="avatar-image"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="avatar-fallback" style={{ display: userAvatars[selectedChat.chatId] ? 'none' : 'flex' }}>
                      {loadingAvatars[selectedChat.chatId] ? (
                        <div className="avatar-loading">📷</div>
                      ) : (
                        selectedChat.userName.charAt(0).toUpperCase()
                      )}
                    </div>
                  </div>
                  <div className="chat-details">
                    <h3
                      className="chat-user-name-link"
                      onClick={() => openUserDetails(selectedChat.chatId)}
                      title="Нажмите, чтобы посмотреть профиль пользователя"
                    >
                      {selectedChat.userName}
                    </h3>
                    {selectedChat.group && <p>Группа: {selectedChat.group}</p>}
                  </div>
                </div>

                {/* Модальное окно деталей пользователя */}
                {(selectedUser || userDetailsLoading) && (
                  <div
                    className="user-details-overlay"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) setSelectedUser(null);
                    }}
                  >
                    <div className="user-details-popup">
                      <button
                        className="user-details-popup-close"
                        onClick={() => setSelectedUser(null)}
                        title="Закрыть"
                      >
                        ×
                      </button>
                      {userDetailsLoading ? (
                        <div className="user-details-popup-loading">
                          <LoadingSpinner />
                        </div>
                      ) : selectedUser ? (
                        <UserDetailsCard user={selectedUser} />
                      ) : (
                        <div className="user-details-popup-not-found">
                          <p>Пользователь не найден</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Список сообщений */}
                <div 
                  className="messages-list" 
                  ref={messagesContainerRef}
                  onScroll={handleScroll}
                >
                  {messagesLoading && messagesPage > 1 && (
                    <div className="loading-more">
                      <LoadingSpinner />
                    </div>
                  )}
                  
                  {messages.map((message) => (
                    <div
                      key={message._id}
                      className={`message ${message.sender === 'bot' ? 'bot-message' : 'user-message'}`}
                    >
                      <div className="message-content">
                        <div className="message-text">
                          {message.parseMode === 'HTML' ? (
                            <div dangerouslySetInnerHTML={{ __html: message.text }} />
                          ) : (
                            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{message.text}</pre>
                          )}
                        </div>
                        <div className="message-time">
                          {formatTime(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {messagesLoading && messagesPage === 1 && (
                    <div className="loading-container">
                      <LoadingSpinner />
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Поле ввода сообщения */}
                <div className="message-input-area">
                  <div className="message-input-options">
                    <div className="parse-mode-selector">
                      <label>Режим разметки:</label>
                      <select 
                        value={parseMode} 
                        onChange={(e) => setParseMode(e.target.value as 'HTML' | 'Markdown')}
                        className="parse-mode-select"
                      >
                        <option value="HTML">HTML</option>
                        <option value="Markdown">Markdown</option>
                      </select>
                    </div>
                  </div>
                  <div className="message-input-container">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Введите сообщение..."
                      rows={1}
                      disabled={sending}
                      className="message-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sending}
                      className="send-button"
                    >
                      {sending ? <LoadingSpinner /> : '📤'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-chat">
                <div className="empty-chat-content">
                  <h3>Выберите чат</h3>
                  <p>Выберите чат из списка слева, чтобы начать общение</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Chats;
